import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { orderStopActions } from "./stopActions";
import { FeedbackFields } from "./WorkoutFeedback";
import usePhoneLandscape from "../../hooks/usePhoneLandscape";

/**
 * "Stop workout?" with save, restart, discard and carry on (`stopActions`,
 * [{ label, onClick, variant?, withFeedback?, cancel? }]). The Coach opens it
 * from its own button (`trigger`); the bike and rower screens' paused bar
 * (#197) opens it from a menu, so it can also be controlled with `open` and
 * `onOpenChange`.
 */
export default function StopWorkoutDialog({
  stopActions,
  collectStopFeedback = false,
  trigger = null,
  open: controlledOpen,
  onOpenChange,
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  // A phone on its side (#227): effort and notes side by side, so every
  // button fits without a scroll.
  const landscape = usePhoneLandscape();
  const open = controlledOpen ?? ownOpen;
  // Optional effort and notes, cleared each time it opens so a previous
  // stop's values don't carry over.
  const [stopRpe, setStopRpe] = useState(null);
  const [stopNotes, setStopNotes] = useState("");
  const handleOpenChange = (next) => {
    if (controlledOpen === undefined) setOwnOpen(next);
    onOpenChange?.(next);
    if (next) {
      setStopRpe(null);
      setStopNotes("");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent
        className={`border-border/50 card-glow ${landscape ? "gap-2 py-4 sm:max-w-2xl" : "sm:max-w-xl"}`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Stop workout?</AlertDialogTitle>
          <AlertDialogDescription>
            {collectStopFeedback
              ? "Add an optional effort rating and notes, then save your progress to history — or discard it."
              : "Would you like to save your progress to history before stopping?"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {collectStopFeedback && (
          <div className="mt-2">
            <FeedbackFields
              rpe={stopRpe}
              onRpeChange={setStopRpe}
              notes={stopNotes}
              onNotesChange={setStopNotes}
              sideBySide={landscape}
            />
          </div>
        )}
        {/* Stacked on a narrow phone; side by side otherwise, wrapping onto a
            second row rather than running past the dialog's edge (landscape). */}
        <AlertDialogFooter className={`${landscape ? "mt-2" : "mt-4"} flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-start sm:space-x-0`}>
          {orderStopActions(stopActions).map((action, i) => {
            // 1. Safe Abort Action (Continue Workout)
            if (action.cancel) {
              return (
                <AlertDialogCancel
                  key={i}
                  className="w-full sm:w-auto mt-0 bg-transparent border border-border hover:bg-muted text-foreground transition-colors"
                >
                  {action.label}
                </AlertDialogCancel>
              );
            }

            // Determine colours based on action intent
            let buttonClass = "";
            if (action.label.toLowerCase().includes("save")) {
              // The action you usually want: the main button.
              buttonClass =
                "bg-primary hover:bg-primary/90 text-primary-foreground";
            } else if (
              action.label.toLowerCase().includes("discard")
            ) {
              // Destructive, so deliberately quiet: it used to be the
              // brightest button in the dialog.
              buttonClass =
                "bg-transparent border border-destructive/50 text-destructive hover:bg-destructive/10";
            } else if (
              action.label.toLowerCase().includes("restart")
            ) {
              // Reset action -> Muted
              buttonClass =
                "bg-muted hover:bg-muted/80 text-foreground border border-transparent";
            }

            return (
              <AlertDialogAction
                key={i}
                onClick={() =>
                  action.withFeedback
                    ? action.onClick({
                        rpe: stopRpe,
                        notes: stopNotes.trim() || undefined,
                      })
                    : action.onClick()
                }
                className={`w-full sm:w-auto transition-colors ${buttonClass}`}
              >
                {action.label}
              </AlertDialogAction>
            );
          })}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
